// PDF utilities for coordinate conversion and field detection
import * as pdfjsLib from 'pdfjs-dist';
import { PDFDocument } from 'pdf-lib';

// Configure PDF.js worker (bundled locally, no CDN dependency)
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.min.js?url';
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

export const loadPdfDocument = async (url, { loadPdfLib = false } = {}) => {
  const response = await fetch(url);
  const arrayBuffer = await response.arrayBuffer();
  
  // Copy the buffer for pdf.js (it transfers/detaches the ArrayBuffer)
  const bufferForPdfJs = arrayBuffer.slice(0);
  
  // Load with PDF.js for rendering
  // Enable cMap and standard font data for proper glyph rendering
  const pdfDoc = await pdfjsLib.getDocument({
    data: bufferForPdfJs,
    cMapUrl: `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/cmaps/`,
    cMapPacked: true,
    standardFontDataUrl: `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/standard_fonts/`
  }).promise;
  
  // Only load pdf-lib when needed (e.g., for form editing, not for patient form viewing)
  let pdfLibDoc = null;
  if (loadPdfLib) {
    const bufferForPdfLib = arrayBuffer.slice(0);
    pdfLibDoc = await PDFDocument.load(bufferForPdfLib);
  }
  
  return {
    pdfDoc,
    pdfLibDoc,
    arrayBuffer
  };
};

export const renderPdfPage = async (pdfDoc, pageNum, canvas, scale = 1.0, textLayerDiv = null) => {
  const page = await pdfDoc.getPage(pageNum);
  
  // Get device pixel ratio for high-DPI rendering
  const dpr = window.devicePixelRatio || 1;
  
  // The scale parameter already considers DPR from usePdfRenderer
  // But we need to render at even higher resolution for the canvas internal buffer
  const renderScale = scale * dpr;
  const viewport = page.getViewport({ scale: renderScale });
  
  const context = canvas.getContext('2d');
  
  // Set canvas internal resolution (high-DPI)
  canvas.width = viewport.width;
  canvas.height = viewport.height;
  
  // Set canvas CSS display size (scaled down by DPR to show at correct size)
  const displayViewport = page.getViewport({ scale });
  canvas.style.width = `${displayViewport.width}px`;
  canvas.style.height = `${displayViewport.height}px`;
  
  const renderTask = page.render({
    canvasContext: context,
    viewport: viewport,
    annotationMode: 0  // DISABLE - we use React overlay for form fields
  });
  
  await renderTask.promise;
  
  // Render text layer for crisp text on mobile devices
  if (textLayerDiv) {
    // Clear previous text layer
    textLayerDiv.innerHTML = '';
    
    // Set text layer dimensions to match display viewport
    textLayerDiv.style.width = `${displayViewport.width}px`;
    textLayerDiv.style.height = `${displayViewport.height}px`;
    
    // Get text content
    const textContent = await page.getTextContent();
    
    // Render text layer using PDF.js text layer builder
    pdfjsLib.renderTextLayer({
      textContentSource: textContent,
      container: textLayerDiv,
      viewport: displayViewport,
      textDivs: []
    });
  }
  
  return { viewport, renderTask, page };
};

export const getFieldType = (field) => {
  const PDFLib = window.PDFLib || {};
  
  if (PDFLib.PDFTextField && field instanceof PDFLib.PDFTextField) {
    return 'text';
  }
  if (PDFLib.PDFCheckBox && field instanceof PDFLib.PDFCheckBox) {
    return 'checkbox';
  }
  if (PDFLib.PDFRadioGroup && field instanceof PDFLib.PDFRadioGroup) {
    return 'radio';
  }
  if (PDFLib.PDFDropdown && field instanceof PDFLib.PDFDropdown) {
    return 'dropdown';
  }
  
  return 'unknown';
};

export const extractPdfFields = async (pdfLibDoc) => {
  const form = pdfLibDoc.getForm();
  const fields = form.getFields();
  const formFields = {};
  
  fields.forEach(field => {
    const fieldName = field.getName();
    const fieldType = getFieldType(field);
    
    formFields[fieldName] = {
      name: fieldName,
      type: fieldType,
      field: field
    };
  });
  
  return formFields;
};

export const convertPdfToViewportCoords = (pdfRect, viewport) => {
  const viewportRect = viewport.convertToViewportRectangle(pdfRect);
  return {
    x: Math.min(viewportRect[0], viewportRect[2]),
    y: Math.min(viewportRect[1], viewportRect[3]),
    width: Math.abs(viewportRect[2] - viewportRect[0]),
    height: Math.abs(viewportRect[3] - viewportRect[1])
  };
};
